/**
 * First-touch UTM capture.
 *
 * The first time a visitor lands on the site with utm_* parameters in the URL we
 * store them for the rest of the browser session, so that a conversion happening
 * a few pages later can still be attributed to the campaign that brought them in.
 *
 * sessionStorage is used on purpose: it is scoped to the tab, cleared when the tab
 * closes, and is not a cookie, so it falls under functional storage rather than
 * marketing storage and does not depend on the cookie consent choice.
 */

export const UTM_PARAMETERS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

export type UtmParameter = (typeof UTM_PARAMETERS)[number];

export type UtmParameters = Partial<Record<UtmParameter, string>>;

const STORAGE_KEY = 'dream-space:utm';

/** Keep values in step with the max length the backend stores. */
const MAX_VALUE_LENGTH = 255;

/**
 * sessionStorage access throws rather than returning null when storage is blocked
 * (private browsing, hardened privacy settings), so every access is guarded.
 */
function getStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function isUtmParameter(key: string): key is UtmParameter {
  return (UTM_PARAMETERS as readonly string[]).includes(key);
}

function sanitize(value: string): string | null {
  const trimmed = value.trim();

  return trimmed === '' ? null : trimmed.slice(0, MAX_VALUE_LENGTH);
}

/** Read the parameters captured earlier in this session. */
export function getUtmParameters(): UtmParameters {
  const storage = getStorage();

  if (!storage) {
    return {};
  }

  try {
    const stored: unknown = JSON.parse(storage.getItem(STORAGE_KEY) ?? '{}');

    if (typeof stored !== 'object' || stored === null) {
      return {};
    }

    const parameters: UtmParameters = {};

    for (const [key, value] of Object.entries(stored)) {
      if (isUtmParameter(key) && typeof value === 'string') {
        const sanitized = sanitize(value);

        if (sanitized) {
          parameters[key] = sanitized;
        }
      }
    }

    return parameters;
  } catch {
    return {};
  }
}

/**
 * Store the utm_* parameters from the current URL, unless this session already
 * captured a set — first touch wins, so the campaign that originally brought the
 * visitor in is the one credited for the conversion.
 */
export function captureUtmParameters(
  search: string = typeof window === 'undefined' ? '' : window.location.search,
): UtmParameters {
  const storage = getStorage();

  if (!storage) {
    return {};
  }

  const alreadyCaptured = getUtmParameters();

  if (Object.keys(alreadyCaptured).length > 0) {
    return alreadyCaptured;
  }

  const searchParams = new URLSearchParams(search);
  const captured: UtmParameters = {};

  for (const parameter of UTM_PARAMETERS) {
    const value = searchParams.get(parameter);

    if (value === null) {
      continue;
    }

    const sanitized = sanitize(value);

    if (sanitized) {
      captured[parameter] = sanitized;
    }
  }

  if (Object.keys(captured).length === 0) {
    return {};
  }

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(captured));
  } catch {
    // Storage is full or blocked; attribution is best effort, so carry on.
  }

  return captured;
}

/** Clear the captured parameters. Exposed for tests and manual resets. */
export function clearUtmParameters(): void {
  try {
    getStorage()?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}
